import React, { useCallback } from 'react'
import styled from 'styled-components'

import Flex from 'components/reusable/flex'
import Input from 'components/reusable/input'
import Text from 'components/reusable/text'
import StripeHelpers from 'helpers/stripe_helpers'
import { colors } from 'helpers/colors'

function PaymentMethodOption({ onSelect, checked, label, disabled }) {
  const handleClick = useCallback(() => {
    if (!disabled && onSelect) {
      onSelect()
    }
  }, [disabled, onSelect])

  return (
    <TooltipWrapper $disabled={disabled}>
      <ButtonWrap onClick={handleClick} $disabled={disabled}>
        <Input type="radio" checked={checked} disabled={disabled} />
        <Text size={[{ maxWidth: 768, size: 12 }, { size: 14 }]}>{label.toUpperCase()}</Text>
      </ButtonWrap>
      {disabled && <TooltipText>Not supported by talent payment capabilities</TooltipText>}
    </TooltipWrapper>
  )
}

function PaymentMethodsList({
  newCard = false,
  selectedPM = null,
  onSelect,
  paymentMethods = [],
  getPaymentMethodDisabled = null,
}) {
  const handleSelect = useCallback(
    (pm) => () => {
      if (onSelect) onSelect(pm)
    },
    [onSelect]
  )

  return (
    <Wrapper dir="column" gap={8} width="100%" jc="flex-start" ai="flex-start">
      {newCard && (
        <PaymentMethodOption
          onSelect={handleSelect('new-card')}
          checked={selectedPM === 'new-card'}
          label="Use a new card"
          disabled={false}
        />
      )}

      {paymentMethods
        .sort((a, b) => {
          const disabledA = getPaymentMethodDisabled ? getPaymentMethodDisabled(a) : false
          const disabledB = getPaymentMethodDisabled ? getPaymentMethodDisabled(b) : false
          return disabledA ? 1 : disabledB ? -1 : 0
        })
        .map((pm) => {
          let label = null
          const checked = selectedPM?.stripe_payment_method_id === pm.stripe_payment_method_id
          const disabled = getPaymentMethodDisabled ? getPaymentMethodDisabled(pm) : false

          if (pm.data.type === 'card') {
            label = StripeHelpers.getCardLabel(pm.data.card)
          } else if (pm.data.type === 'us_bank_account') {
            label = StripeHelpers.getUSBankAccountLabel(pm.data.us_bank_account)
          }

          if (!label) return null

          return (
            <PaymentMethodOption
              key={pm.stripe_payment_method_id}
              onSelect={handleSelect(pm)}
              checked={checked}
              label={label}
              disabled={disabled}
            />
          )
        })}
    </Wrapper>
  )
}

const Wrapper = styled(Flex)`
  padding-top: 40px; // to make disabled PM tips visible inside overflow
`

const TooltipWrapper = styled.div`
  position: relative;
  width: 100%;
`

const ButtonWrap = styled(Flex)`
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 12px;
  width: 100%;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
`

const TooltipText = styled.span`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 100;
  visibility: hidden;
  width: max-content;
  max-width: 280px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${colors.gray[50]};
  border: 1px solid ${colors.gray[200]};
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  color: ${colors.gray[700]};
  white-space: normal;
  pointer-events: none;

  ${TooltipWrapper}:hover & {
    visibility: visible;
  }

  @media (max-width: 768px) {
    max-width: 240px;
    font-size: 11px;
    padding: 10px 14px;
  }
`

export default PaymentMethodsList
